import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  HiDocument, HiCog6Tooth,
  HiUserGroup, HiBriefcase, HiChevronDown, HiChevronRight, HiScale,
  HiBuildingLibrary, HiAcademicCap, HiAdjustmentsHorizontal,
} from 'react-icons/hi2';
import type { IconType } from 'react-icons';
import type { RootState } from '@/store/store';
import { useListCourtsQuery } from '@/store/sprawyApi';
import {
  SidebarAside,
  Brand,
  BrandLogo,
  Nav,
  Footer,
  NavItemLink,
  NavIcon,
  NavLabel,
  CourtCount,
  SubNavContainer,
  Overlay,
  ChevronToggle,
} from './Sidebar.styled';

interface NavItem {
  path: string;
  labelKey: string;
  Icon: IconType;
  adminOnly?: boolean;
  expandable?: boolean;
}

const navItems: NavItem[] = [
  { path: '/sprawy', labelKey: 'nav.sprawy', Icon: HiBriefcase, expandable: true },
  { path: '/documents', labelKey: 'nav.documents', Icon: HiDocument },
  { path: '/customers', labelKey: 'nav.customers', Icon: HiUserGroup },
  { path: '/pelnomocnicy', labelKey: 'nav.pelnomocnicy', Icon: HiScale },
  { path: '/courts', labelKey: 'nav.courts', Icon: HiBuildingLibrary },
  { path: '/legal-professionals', labelKey: 'nav.legalDirectory', Icon: HiAcademicCap },
  { path: '/settings', labelKey: 'nav.settings', Icon: HiAdjustmentsHorizontal },
  { path: '/admin', labelKey: 'nav.admin', Icon: HiCog6Tooth, adminOnly: true },
];

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();
  const { t } = useTranslation();
  const isAdmin = useSelector((state: RootState) => state.auth.user?.permissions?.includes('admin') ?? false);
  const [sprawyExpanded, setSprawyExpanded] = useState(false);
  const { data: apelacje } = useListCourtsQuery();

  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <>
      <SidebarAside $isOpen={isOpen} data-surface="navy">
        <Brand>
          <BrandLogo src="/logo_32_32.png" alt="Scribia" />
          <h1>{t('sidebar.brand')}</h1>
        </Brand>
        <Nav>
          {visibleItems.map((item) => {
            const isActive = location.pathname.startsWith(item.path);
            return (
              <div key={item.path}>
                <NavItemLink
                  to={item.path}
                  $active={isActive}
                  onClick={(e) => {
                    if (item.expandable && apelacje && apelacje.length > 0) {
                      e.preventDefault();
                      setSprawyExpanded(!sprawyExpanded);
                    } else {
                      onClose();
                    }
                  }}
                >
                  <NavIcon><item.Icon /></NavIcon>
                  <NavLabel>{t(item.labelKey)}</NavLabel>
                  {item.expandable && apelacje && apelacje.length > 0 && (
                    <ChevronToggle>
                      {sprawyExpanded ? <HiChevronDown size="0.875rem" /> : <HiChevronRight size="0.875rem" />}
                    </ChevronToggle>
                  )}
                </NavItemLink>
                {item.expandable && sprawyExpanded && apelacje && (
                  <SubNavContainer>
                    <NavItemLink
                      to="/sprawy/calendar"
                      $active={location.pathname === '/sprawy/calendar'}
                      $sub
                      onClick={onClose}
                    >
                      <NavLabel>{t('nav.calendar')}</NavLabel>
                    </NavItemLink>
                    <NavItemLink
                      to="/sprawy"
                      $active={location.pathname === '/sprawy' && !location.search}
                      $sub
                      onClick={onClose}
                    >
                      <NavLabel>{t('nav.allCourts')}</NavLabel>
                    </NavItemLink>
                    {apelacje.map((a) => (
                      <NavItemLink
                        key={a.apelacja}
                        to={`/sprawy?apelacja=${encodeURIComponent(a.apelacja)}`}
                        $active={location.search === `?apelacja=${encodeURIComponent(a.apelacja)}`}
                        $sub
                        onClick={onClose}
                      >
                        <NavLabel>
                          {t(`apelacja.${a.apelacja}`, a.apelacja)}
                          <CourtCount>{a.sprawyCount}</CourtCount>
                        </NavLabel>
                      </NavItemLink>
                    ))}
                  </SubNavContainer>
                )}
              </div>
            );
          })}
        </Nav>
        <Footer>
          <span>{t('sidebar.footer')}</span>
        </Footer>
      </SidebarAside>
      <Overlay $visible={isOpen} onClick={onClose} role="presentation" />
    </>
  );
}
